import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { makeRequest } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import {
	BulkAssignCounterpartyJobStatus,
	type BulkAssignCounterpartyJobStatusState,
} from '@dealstack/shared/lib/common/bulkAssignCounterparty/jobStatus';

const POLL_INTERVAL_MS = 2000;

const TERMINAL_STATUSES: readonly BulkAssignCounterpartyJobStatus[] = [
	BulkAssignCounterpartyJobStatus.COMPLETED,
	BulkAssignCounterpartyJobStatus.FAILED,
];

const buildStatusEndpoint = (dealId: string, programmeId: string, jobId: string): string =>
	`${formatUnicorn(API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_BULK_ASSIGN_COUNTERPARTY_POST, { dealId, programmeId })}-status/${jobId}`;

export const useBulkAssignCounterpartyStatus = ({
	dealId,
	programmeId,
	jobId,
	onTerminal,
}: {
	dealId: string;
	programmeId: string;
	jobId: string | null;
	onTerminal?: (state: BulkAssignCounterpartyJobStatusState) => void;
}) => {
	const queryClient = useQueryClient();
	const documentListKey = formatUnicorn(
		API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_LIST_GET,
		{ dealId, programmeId }
	);

	const q = useQuery({
		queryKey: ['bulk-assign-counterparty-status', dealId, programmeId, jobId],
		queryFn: async () => {
			const endpoint = buildStatusEndpoint(dealId, programmeId, jobId as string);
			const response = await makeRequest<
				ApiV1.Response<BulkAssignCounterpartyJobStatusState>
			>({
				endpoint,
				method: HTTP_METHOD.GET,
			});
			return response.payload as BulkAssignCounterpartyJobStatusState;
		},
		enabled: !!jobId,
		refetchInterval: (query) => {
			const data = query.state.data;
			if (!data) {
				return POLL_INTERVAL_MS;
			}
			if (TERMINAL_STATUSES.includes(data.status)) {
				return false;
			}
			return POLL_INTERVAL_MS;
		},
	});

	useEffect(() => {
		if (!q.isSuccess) {
			return;
		}
		void (async () => {
			const data = q.data;
			if (TERMINAL_STATUSES.includes(data.status)) {
				await queryClient.invalidateQueries({ queryKey: [documentListKey] });
				onTerminal?.(data);
			}
		})();
	}, [q.isSuccess, q.data]);

	return q;
};
